import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ListTodo, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { fetchAllTasks } from '../api/projects';
import { fetchContacts, fetchPipelineStages } from '../api/crm';
import { fetchPeople } from '../api/people';
import type { Task, TaskStatus } from '../types/project';
import type { Contact, PipelineStage } from '../types/crm';
import type { Person } from '../types/people';
import { STATUS_LABEL, STATUS_BADGE } from '../utils/taskStatus';
import { projectLabel } from '../utils/task';
import { formatDateDDMMYYYY, formatRelativeTime } from '../utils/date';
import Avatar from '../components/Avatar';

const QUOTES = [
  'The best way out is always through.',
  'Done is better than perfect.',
  'Small daily improvements compound into stunning results.',
  'You can do anything, but not everything.',
  'Discipline equals freedom.',
  'Focus is saying no to a thousand good ideas.',
  'Slow is smooth, smooth is fast.',
  'Progress over perfection.',
  'Make the thing that matters today.',
  'Action is the antidote to anxiety.',
  'Begin where you are. Use what you have. Do what you can.',
  'Energy follows attention.',
  'Hard choices, easy life.',
  'Ship it.',
  'Measure twice, cut once.',
  'The standard you walk past is the standard you accept.',
  'Compounding rewards consistency.',
  'Cut the obvious problem first.',
  "Tomorrow's results come from today's habits.",
  'A bad plan executed well beats a perfect plan executed poorly.',
];

const QUOTE_STORAGE_KEY = 'jambo:dashboard:lastQuoteIdx';

function pickQuoteIndex(): number {
  if (QUOTES.length <= 1) return 0;
  let lastIdx = -1;
  try {
    const stored = sessionStorage.getItem(QUOTE_STORAGE_KEY);
    if (stored !== null) lastIdx = parseInt(stored, 10);
  } catch {
    // sessionStorage unavailable
  }
  let next = Math.floor(Math.random() * QUOTES.length);
  if (next === lastIdx) next = (next + 1) % QUOTES.length;
  try {
    sessionStorage.setItem(QUOTE_STORAGE_KEY, String(next));
  } catch {
    // ignore
  }
  return next;
}

function greetingFor(name: string | null): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const who = name?.trim() || 'there';
  return `Good ${part}, ${who}`;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysISO(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const cardStyle: React.CSSProperties = {
  background: 'white',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: '20px 22px',
  boxShadow: 'var(--shadow-sm)',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  minHeight: '320px',
};

const cardTitleStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-secondary)',
};

const viewAllLinkStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-blue-dark)',
  textDecoration: 'none',
};

const emptyStateStyle: React.CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: '14px',
  padding: '12px 4px',
};

const errorStyle: React.CSSProperties = {
  color: 'var(--color-coral)',
  fontSize: '13px',
  padding: '12px 4px',
};

const subHeadStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-text-muted)',
  marginTop: '4px',
};

const rowBaseStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '8px 6px',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  textDecoration: 'none',
  color: 'inherit',
};

const moreLineStyle: React.CSSProperties = {
  fontSize: '12px',
  color: 'var(--color-text-muted)',
  padding: '4px 6px',
};

function CardHeader({ title, viewAll }: { title: string; viewAll?: { to: string; label?: string } }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
      <h2 style={{ ...cardTitleStyle, margin: 0 }}>{title}</h2>
      {viewAll && (
        <Link to={viewAll.to} style={viewAllLinkStyle}>
          {viewAll.label ?? 'View all'} →
        </Link>
      )}
    </div>
  );
}

function SkeletonRows({ count = 3 }: { count?: number }) {
  const widths = ['85%', '60%', '75%', '50%', '70%'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '4px' }}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            height: '14px',
            width: widths[i % widths.length],
            background: 'rgba(0,0,0,0.06)',
            borderRadius: '4px',
          }}
        />
      ))}
    </div>
  );
}

interface RowWrapperProps {
  to: string;
  children: React.ReactNode;
}

function RowLink({ to, children }: RowWrapperProps) {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={to}
      style={{
        ...rowBaseStyle,
        background: hover ? 'var(--color-warm-bg)' : 'transparent',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {children}
    </Link>
  );
}

export default function DashboardPage() {
  const { auth } = useAuth();
  const personId = auth.status === 'authenticated' ? auth.personId : null;
  const name = auth.status === 'authenticated' ? auth.name : null;

  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [stages, setStages] = useState<PipelineStage[] | null>(null);
  const [crmError, setCrmError] = useState<string | null>(null);
  const [people, setPeople] = useState<Person[]>([]);

  const greeting = useMemo(() => greetingFor(name), [name]);
  const quote = useMemo(() => QUOTES[pickQuoteIndex()], []);

  useEffect(() => {
    fetchAllTasks()
      .then(setTasks)
      .catch((e: Error) => setTasksError(e.message));
    Promise.all([fetchContacts(false), fetchPipelineStages()])
      .then(([c, s]) => {
        setContacts(c);
        setStages(s);
      })
      .catch((e: Error) => setCrmError(e.message));
    fetchPeople().then(setPeople).catch(() => {});
  }, []);

  const peopleById = useMemo(() => {
    const m = new Map<string, Person>();
    for (const p of people) m.set(p.id, p);
    return m;
  }, [people]);

  // ----- MY TASKS -----
  const today = todayISO();
  const inSeven = useMemo(() => addDaysISO(today, 7), [today]);

  const { myOverdue, myDueSoon } = useMemo(() => {
    if (!tasks || !personId) return { myOverdue: [] as Task[], myDueSoon: [] as Task[] };
    const isMine = (t: Task) =>
      t.assignee === personId || (t.assignee_ids?.includes(personId) ?? false);
    const open = tasks.filter(
      (t) => isMine(t) && t.status !== 'COMPLETED' && t.status !== 'CANCELLED'
    );
    const myOverdue = open
      .filter((t) => t.due_date && t.due_date < today)
      .sort((a, b) => (a.due_date ?? '').localeCompare(b.due_date ?? ''));
    const myDueSoon = open
      .filter((t) => t.due_date && t.due_date >= today && t.due_date <= inSeven)
      .sort((a, b) => (a.due_date ?? '').localeCompare(b.due_date ?? ''));
    return { myOverdue, myDueSoon };
  }, [tasks, personId, today, inSeven]);

  // ----- WHAT'S SLIPPING -----
  const allOverdue = useMemo(() => {
    if (!tasks) return [] as Task[];
    return tasks
      .filter(
        (t) =>
          t.status !== 'COMPLETED' &&
          t.status !== 'CANCELLED' &&
          t.due_date &&
          t.due_date < today
      )
      .sort((a, b) => (a.due_date ?? '').localeCompare(b.due_date ?? ''));
  }, [tasks, today]);

  // ----- CRM PULSE -----
  const crmPulse = useMemo(() => {
    if (!contacts || !stages)
      return { dueToday: [] as Contact[], overdue: [] as Contact[], proposal: [] as Contact[] };
    const dueToday = contacts.filter((c) => c.next_follow_up_date === today);
    const overdue = contacts.filter(
      (c) => c.next_follow_up_date && c.next_follow_up_date < today
    );
    const proposalStageId = stages.find((s) => s.name === 'Proposal Sent')?.id;
    const proposal = proposalStageId
      ? contacts.filter((c) => c.stage_id === proposalStageId)
      : [];
    return { dueToday, overdue, proposal };
  }, [contacts, stages, today]);

  // ----- RECENT ACTIVITY -----
  type ActivityItem = {
    kind: 'task' | 'contact';
    id: string;
    label: string;
    href: string;
    created_at: string;
  };
  const recentActivity = useMemo<ActivityItem[]>(() => {
    if (!tasks && !contacts) return [];
    const items: ActivityItem[] = [];
    if (tasks) {
      for (const t of tasks) {
        items.push({
          kind: 'task',
          id: t.id,
          label: `New task: ${t.title}`,
          href: `/projects/task/${t.id}`,
          created_at: t.created_at,
        });
      }
    }
    if (contacts) {
      for (const c of contacts) {
        items.push({
          kind: 'contact',
          id: c.id,
          label: `New contact: ${c.first_name} ${c.last_name}`,
          href: `/crm/contact/${c.id}`,
          created_at: c.created_at,
        });
      }
    }
    items.sort((a, b) => b.created_at.localeCompare(a.created_at));
    return items.slice(0, 10);
  }, [tasks, contacts]);

  const tasksLoading = tasks === null && !tasksError;
  const crmLoading = (contacts === null || stages === null) && !crmError;

  return (
    <>
      <div style={{ marginBottom: '28px' }}>
        <h1
          style={{
            fontSize: '28px',
            fontWeight: 700,
            margin: '0 0 4px',
            letterSpacing: '-0.01em',
          }}
        >
          {greeting}
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: '14px',
            color: 'var(--color-text-muted)',
            fontStyle: 'italic',
          }}
        >
          {quote}
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
          gap: '24px',
        }}
      >
        {/* MY TASKS */}
        <section style={cardStyle}>
          <CardHeader title="My tasks" viewAll={{ to: '/tasks/list' }} />
          {tasksError && <div style={errorStyle}>Couldn't load tasks.</div>}
          {!tasksError && tasksLoading && <SkeletonRows />}
          {!tasksError && !tasksLoading && myOverdue.length === 0 && myDueSoon.length === 0 && (
            <div style={emptyStateStyle}>Nothing on your plate. Enjoy it.</div>
          )}
          {!tasksError && !tasksLoading && (myOverdue.length > 0 || myDueSoon.length > 0) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {myOverdue.length > 0 && (
                <>
                  <div style={{ ...subHeadStyle, color: 'var(--color-coral)' }}>
                    Overdue ({myOverdue.length})
                  </div>
                  {myOverdue.slice(0, 5).map((t) => (
                    <MyTaskRow key={t.id} task={t} highlight />
                  ))}
                  {myOverdue.length > 5 && (
                    <div style={moreLineStyle}>+{myOverdue.length - 5} more</div>
                  )}
                </>
              )}
              {myDueSoon.length > 0 && (
                <>
                  <div style={subHeadStyle}>Due in next 7 days ({myDueSoon.length})</div>
                  {myDueSoon.slice(0, 5).map((t) => (
                    <MyTaskRow key={t.id} task={t} />
                  ))}
                  {myDueSoon.length > 5 && (
                    <div style={moreLineStyle}>+{myDueSoon.length - 5} more</div>
                  )}
                </>
              )}
            </div>
          )}
        </section>

        {/* WHAT'S SLIPPING */}
        <section style={cardStyle}>
          <CardHeader title="What's slipping" viewAll={{ to: '/tasks/board' }} />
          {tasksError && <div style={errorStyle}>Couldn't load tasks.</div>}
          {!tasksError && tasksLoading && <SkeletonRows />}
          {!tasksError && !tasksLoading && allOverdue.length === 0 && (
            <div style={emptyStateStyle}>Nothing overdue across the team. Quietly impressive.</div>
          )}
          {!tasksError && !tasksLoading && allOverdue.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {allOverdue.slice(0, 8).map((t) => {
                const assigneeId = t.assignee ?? t.assignee_ids?.[0] ?? null;
                const person = assigneeId ? peopleById.get(assigneeId) ?? null : null;
                return <SlippingRow key={t.id} task={t} assigneeName={person?.name} />;
              })}
              {allOverdue.length > 8 && (
                <div style={moreLineStyle}>+{allOverdue.length - 8} more</div>
              )}
            </div>
          )}
        </section>

        {/* CRM PULSE */}
        <section style={cardStyle}>
          <CardHeader title="CRM pulse" viewAll={{ to: '/crm' }} />
          {crmError && <div style={errorStyle}>Couldn't load CRM data.</div>}
          {!crmError && crmLoading && <SkeletonRows />}
          {!crmError && !crmLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <CrmPulseSection
                heading="Due today"
                count={crmPulse.dueToday.length}
                contacts={crmPulse.dueToday}
                accent={false}
              />
              <CrmPulseSection
                heading="Overdue"
                count={crmPulse.overdue.length}
                contacts={crmPulse.overdue}
                accent
              />
              <CrmPulseSection
                heading="Proposal Sent"
                count={crmPulse.proposal.length}
                contacts={crmPulse.proposal}
                accent={false}
              />
            </div>
          )}
        </section>

        {/* RECENT ACTIVITY */}
        <section style={cardStyle}>
          <CardHeader title="Recent activity" />
          {tasksError && crmError && <div style={errorStyle}>Couldn't load activity.</div>}
          {!tasksError && !crmError && (tasksLoading || crmLoading) && <SkeletonRows count={5} />}
          {!tasksLoading && !crmLoading && recentActivity.length === 0 && (
            <div style={emptyStateStyle}>Nothing new yet.</div>
          )}
          {!tasksLoading && !crmLoading && recentActivity.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {recentActivity.map((item) => (
                <RowLink key={`${item.kind}:${item.id}`} to={item.href}>
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background:
                        item.kind === 'task' ? 'rgba(74, 155, 196, 0.14)' : 'rgba(217, 90, 69, 0.14)',
                      color:
                        item.kind === 'task' ? 'var(--color-blue-accent)' : 'var(--color-coral)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                    aria-hidden
                  >
                    {item.kind === 'task' ? <ListTodo size={14} /> : <UserPlus size={14} />}
                  </div>
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: '13px',
                      color: 'var(--color-text-primary)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'var(--color-text-muted)',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {formatRelativeTime(item.created_at)}
                  </div>
                </RowLink>
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function MyTaskRow({ task, highlight = false }: { task: Task; highlight?: boolean }) {
  const project = projectLabel(task);
  const badge = STATUS_BADGE[task.status as TaskStatus];
  return (
    <RowLink to={`/projects/task/${task.id}`}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--color-text-primary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {task.title}
        </div>
        {project && (
          <div
            style={{
              fontSize: '12px',
              color: 'var(--color-text-muted)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {project}
          </div>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '4px',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: '11px',
            fontWeight: 600,
            background: badge.bg,
            color: badge.color,
            padding: '2px 8px',
            borderRadius: '999px',
          }}
        >
          {STATUS_LABEL[task.status as TaskStatus]}
        </span>
        {task.due_date && (
          <span
            style={{
              fontSize: '12px',
              color: highlight ? 'var(--color-coral)' : 'var(--color-text-muted)',
              fontWeight: highlight ? 600 : 400,
            }}
          >
            {formatDateDDMMYYYY(task.due_date)}
          </span>
        )}
      </div>
    </RowLink>
  );
}

function SlippingRow({ task, assigneeName }: { task: Task; assigneeName?: string | null }) {
  const project = projectLabel(task);
  return (
    <RowLink to={`/projects/task/${task.id}`}>
      <Avatar name={assigneeName} size={28} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--color-text-primary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {task.title}
        </div>
        {project && (
          <div
            style={{
              fontSize: '12px',
              color: 'var(--color-text-muted)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {project}
          </div>
        )}
      </div>
      <span
        style={{
          fontSize: '12px',
          color: 'var(--color-coral)',
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {task.due_date ? formatDateDDMMYYYY(task.due_date) : ''}
      </span>
    </RowLink>
  );
}

function CrmPulseSection({
  heading,
  count,
  contacts,
  accent,
}: {
  heading: string;
  count: number;
  contacts: Contact[];
  accent: boolean;
}) {
  return (
    <div>
      <div
        style={{
          ...subHeadStyle,
          color: accent && count > 0 ? 'var(--color-coral)' : 'var(--color-text-muted)',
          marginBottom: '6px',
        }}
      >
        {heading} ({count})
      </div>
      {count === 0 ? (
        <div style={{ ...moreLineStyle, padding: '2px 6px' }}>—</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {contacts.slice(0, 3).map((c) => (
            <RowLink key={c.id} to={`/crm/contact/${c.id}`}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--color-text-primary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {c.first_name} {c.last_name}
                </div>
                {c.company && (
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'var(--color-text-muted)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {c.company}
                  </div>
                )}
              </div>
            </RowLink>
          ))}
          {count > 3 && <div style={moreLineStyle}>+{count - 3} more</div>}
        </div>
      )}
    </div>
  );
}
